import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useLocalNavigate as useNavigate } from '@/lib/locale';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ShieldCheck, Lock, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { sanitizeHtml } from '@/lib/html';
import { ProductImage } from '@/lib/images';
import { useInitialData } from '@/lib/initialData';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { formatPrice } from '@/lib/catalog';
import { ProductStructuredData } from '@/components/ProductStructuredData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductDetail = ({ onAddToCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const initial = useInitialData();
  const seeded = initial.product && initial.product.product_id === id ? initial.product : null;
  const [product, setProduct] = useState(seeded);
  const [loading, setLoading] = useState(!seeded);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(() => seeded?.variants?.[0] || null);
  // A variant's own photo wins until the shopper picks a gallery thumbnail
  const [showVariantImage, setShowVariantImage] = useState(true);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
        // Pre-select first variant if available
        const variants = response.data?.variants;
        if (variants && variants.length > 0) {
          setSelectedVariant(variants[0]);
        }
      } catch (error) {
        console.error('Failed to fetch product:', error);
        toast.error(i18n.t('pdp.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (product?.name) document.title = t('meta.productTitle', { name: product.name });
  }, [product, t]);

  const images = useMemo(() => {
    if (!product) return [];
    const arr = (product.images && product.images.length > 0)
      ? product.images
      : (product.image_url ? [product.image_url] : []);
    return arr;
  }, [product]);

  const activeImageUrl = useMemo(() => {
    const variantImg = selectedVariant?.image_url || null;
    if (variantImg && showVariantImage) return variantImg;
    return images[activeImage] || '';
  }, [selectedVariant, images, activeImage, showVariantImage]);

  // Gallery photos plus variant photos that aren't in the gallery
  const slides = useMemo(() => {
    const extra = (product?.variants || []).map((v) => v.image_url).filter(Boolean);
    return [...new Set([...images, ...extra])];
  }, [images, product]);
  const firstSlide = activeImageUrl || slides[0];

  // Picking a variant shows its photo (and highlights it in the gallery if it's there)
  const selectVariant = (v) => {
    setSelectedVariant(v);
    const idx = v?.image_url ? images.indexOf(v.image_url) : -1;
    if (idx >= 0) setActiveImage(idx);
    setShowVariantImage(true);
  };

  const effectivePrice = useMemo(() => {
    if (!product) return 0;
    const base = product.price || 0;
    const delta = selectedVariant?.price_delta || 0;
    return base + delta;
  }, [product, selectedVariant]);

  if (loading) {
    return (
      <div className="min-h-screen pt-10 pb-20 flex items-center justify-center">
        <p className="text-muted-foreground">{t('pdp.loading')}</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-10 pb-20 flex items-center justify-center">
        <p className="text-muted-foreground">{t('pdp.notFound')}</p>
      </div>
    );
  }

  const hasVariants = product.variants && product.variants.length > 0;

  // Resolve the effective stock for the current selection.
  // - With variants: use selected variant's stock (null/undefined = unlimited)
  // - Without variants: use product.stock
  const effectiveStock = hasVariants
    ? (selectedVariant?.stock ?? null)
    : (product.stock ?? null);
  const isOutOfStock = effectiveStock !== null && effectiveStock <= 0;
  const isLowStock = effectiveStock !== null && effectiveStock > 0 && effectiveStock <= 5;

  const handleAdd = () => {
    if (isOutOfStock) {
      toast.error(t('pdp.variantSoldOutToast'));
      return;
    }
    const productWithSelection = {
      ...product,
      price: effectivePrice,
      // When a variant has its own image, override the cart row image
      image_url: selectedVariant?.image_url || product.image_url,
      selected_variant: selectedVariant ? selectedVariant.label : null,
    };
    onAddToCart(productWithSelection);
  };

  return (
    <div className="min-h-screen pt-6 md:pt-10 pb-20" data-testid="product-detail-page">
      <ProductStructuredData product={product} />
      <div className="container mx-auto px-4 md:px-12">
        <button
          data-testid="back-button"
          onClick={() => navigate('/shop')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6 md:mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm md:text-base">{t('pdp.back')}</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16">
          {/* Gallery */}
          <div className="space-y-3 md:space-y-4">
            <div className="relative aspect-square rounded-2xl overflow-hidden border-2 border-border">
              {/* Every photo is in the DOM (stacked) so it is already loaded when a
                  thumbnail is tapped: switching is instant instead of waiting
                  seconds for the next rendition with the old photo still showing. */}
              {slides.map((u) => {
                const active = u === activeImageUrl;
                return (
                  <ProductImage
                    key={u}
                    src={u}
                    alt={active ? product.name : ''}
                    aria-hidden={active ? undefined : true}
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    width="800"
                    height="800"
                    priority={u === firstSlide}
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-200 ${active ? 'opacity-100' : 'opacity-0'}`}
                    data-testid={active ? 'product-image' : undefined}
                  />
                );
              })}
              <div className="absolute top-3 right-3 md:top-6 md:right-6 bg-accent/90 backdrop-blur-sm text-white text-xs md:text-sm font-semibold px-3 py-1.5 md:px-4 md:py-2 rounded-full">
                {t('product.limited')}
              </div>
            </div>

            {images.length > 1 && (
              <div className="grid grid-cols-5 md:grid-cols-6 gap-2" data-testid="product-thumbnails">
                {images.map((u, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setActiveImage(i);
                      setShowVariantImage(false);
                    }}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      i === activeImage ? 'border-accent ring-2 ring-accent/30' : 'border-border hover:border-accent/50'
                    }`}
                    data-testid={`thumbnail-${i}`}
                  >
                    <ProductImage src={u} alt={`${product.name} — ${t('pdp.view', { n: i + 1 })}`} widths={[200]} sizes="96px" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="hidden md:grid grid-cols-2 gap-3 pt-2">
              <div className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border">
                <Package className="w-5 h-5 text-accent" />
                <div className="text-sm">
                  <div className="font-semibold text-foreground">{t('pdp.fastDelivery')}</div>
                  <div className="text-muted-foreground text-xs">{t('pdp.fastDeliveryNote')}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border">
                <Lock className="w-5 h-5 text-accent" />
                <div className="text-sm">
                  <div className="font-semibold text-foreground">{t('pdp.discreet')}</div>
                  <div className="text-muted-foreground text-xs">{t('pdp.plainPackaging')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-6 md:space-y-8">
            <div>
              <h1 className="text-2xl md:text-5xl font-heading font-bold text-foreground mb-2 md:mb-4" data-testid="product-name">
                {product.name}
              </h1>
              {product.subtitle && (
                <p className="text-xs md:text-sm font-semibold text-accent uppercase tracking-wider mb-3">
                  {product.subtitle}
                </p>
              )}
              <p className="text-3xl md:text-4xl font-bold text-accent mb-4 md:mb-6" data-testid="product-price">
                {formatPrice(effectivePrice)}
              </p>
              <div
                className="product-description text-sm md:text-base text-muted-foreground leading-relaxed"
                data-testid="product-description"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description) }}
              />
            </div>

            {/* Variants selector */}
            {hasVariants && (
              <div className="space-y-2" data-testid="variants-selector">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-sm md:text-base font-semibold text-foreground">{t('pdp.size')}</h3>
                  {selectedVariant && (
                    <span className="text-xs text-muted-foreground">{selectedVariant.label}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((v, i) => {
                    const isActive = selectedVariant?.label === v.label;
                    const vStock = v.stock;
                    const vOut = vStock !== null && vStock !== undefined && vStock <= 0;
                    return (
                      <button
                        key={`${v.label}-${i}`}
                        type="button"
                        onClick={() => selectVariant(v)}
                        disabled={vOut}
                        data-testid={`variant-${v.label.replace(/\s+/g, '-')}`}
                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-all relative ${
                          vOut
                            ? 'border-border bg-muted text-muted-foreground line-through cursor-not-allowed opacity-60'
                            : isActive
                              ? 'bg-accent text-accent-foreground border-accent'
                              : 'border-border bg-card text-foreground hover:border-accent'
                        }`}
                      >
                        {v.label}
                        {v.price_delta && v.price_delta !== 0 ? (
                          <span className="ml-2 text-xs opacity-80">
                            {v.price_delta > 0 ? '+' : ''}€{v.price_delta.toFixed(2)}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>

                {/* Urgency banner per selected variant */}
                {selectedVariant && isOutOfStock && (
                  <div
                    data-testid="variant-out-of-stock"
                    className="mt-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/40 text-red-500 text-xs md:text-sm font-medium"
                  >
                    {t('pdp.variantSoldOut', { size: selectedVariant.label })}
                  </div>
                )}
                {selectedVariant && isLowStock && (
                  <div
                    data-testid="variant-low-stock"
                    className="mt-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-500 text-xs md:text-sm font-medium flex items-center gap-2"
                  >
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                    {t('pdp.variantLowStock', { count: effectiveStock, size: selectedVariant.label })}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3 md:space-y-6">
              <div className="p-4 md:p-6 rounded-xl bg-card border border-border space-y-2">
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">{t('pdp.details')}</h3>
                <div className="space-y-1.5 text-xs md:text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('pdp.category')}:</span>
                    <span className="text-foreground font-semibold capitalize">{t(`categoryNames.${product.category || 'adult-toys'}`, { defaultValue: (product.category || 'adult-toys').replace(/-/g, ' ') })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('pdp.stock')}:</span>
                    <span
                      data-testid="stock-indicator"
                      className={`font-semibold ${isOutOfStock ? 'text-red-500' : isLowStock ? 'text-amber-500' : 'text-accent'}`}
                    >
                      {isOutOfStock
                        ? t('product.soldOut')
                        : isLowStock
                          ? `${t('pdp.onlyLeft', { count: effectiveStock })}${selectedVariant ? ` · ${selectedVariant.label}` : ''}`
                          : t('pdp.inStock')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('pdp.delivery')}:</span>
                    <span className="text-foreground font-semibold">{product.delivery_time_text || t('pdp.fastDelivery')}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 md:p-6 rounded-xl bg-accent/10 border-2 border-accent/30">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent/20 flex items-center justify-center">
                    <span className="text-accent font-bold text-base md:text-lg">18+</span>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-foreground font-semibold mb-1">{t('pdp.adultsOnly')}</p>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      {t('pdp.adultsOnlyText')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Button
              data-testid="add-to-cart-button"
              onClick={handleAdd}
              disabled={isOutOfStock}
              size="lg"
              className={`w-full font-semibold py-6 md:py-8 text-base md:text-xl rounded-xl transition-all duration-300 ${
                isOutOfStock
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-accent text-accent-foreground hover:bg-accent/90 '
              }`}
            >
              {isOutOfStock ? t('product.soldOut') : t('pdp.addToCartPrice', { price: formatPrice(effectivePrice) })}
            </Button>

            <div className="flex items-center justify-center gap-6 pt-2 text-xs md:text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 md:w-5 md:h-5 text-accent" />
                <span>{t('pdp.secureCheckout')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 md:w-5 md:h-5 text-accent" />
                <span>{t('pdp.privateShipping')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
